import type { ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { ArrowsClockwise, Lock, MagnifyingGlass, Question } from '@phosphor-icons/react';
import { Button, buttonClassName } from './button';
import { StateBlock } from './empty-state';

/** The staff app lives under /subdomain/admin (rewritten from admin.*); everything else is the client portal. */
export function useHomePath(): '/subdomain/admin' | '/subdomain/app' {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return pathname.startsWith('/subdomain/admin') ? '/subdomain/admin' : '/subdomain/app';
}

/** Something failed to load (Figma P01 "Error · retry"). `onRetry` refetches; without it the page reloads its data. */
export function ErrorState({
  title = 'Couldn’t load this',
  description = 'Check your connection and try again. If it keeps happening, message your team.',
  onRetry,
  retrying = false,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}) {
  const router = useRouter();
  return (
    <StateBlock
      icon={ArrowsClockwise}
      title={title}
      description={description}
      className={className}
      action={
        <Button
          variant="secondary"
          size="state"
          loading={retrying}
          onClick={onRetry ?? (() => void router.invalidate())}
        >
          Try again
        </Button>
      }
    />
  );
}

/** A search or filter matched nothing (Figma P01 "No search results"). */
export function NoResults({
  query,
  description = 'Try fewer words, or clear the filters.',
  onClear,
  className,
}: {
  /** What was searched; the title quotes it. Left out when only filters are on. */
  query?: string;
  description?: string;
  onClear?: () => void;
  className?: string;
}) {
  return (
    <StateBlock
      icon={MagnifyingGlass}
      title={query ? `No results for “${query}”` : 'No results'}
      description={description}
      className={className}
      action={
        onClear ? (
          <Button variant="secondary" size="state" onClick={onClear}>
            Clear search
          </Button>
        ) : undefined
      }
    />
  );
}

/** Signed in, but this belongs to someone else (Figma P01 "No access · 403", drawn inside the app shell). */
export function Forbidden({
  title = 'You don’t have access to this',
  description = 'It belongs to another client or team. If you think that’s wrong, ask an admin.',
  onBack,
  action,
  className,
}: {
  title?: string;
  description?: string;
  onBack?: () => void;
  /** Replaces "Go back" with a more useful way out (a link to the list this page came from). */
  action?: ReactNode;
  className?: string;
}) {
  const router = useRouter();
  return (
    <StateBlock
      icon={Lock}
      title={title}
      description={description}
      className={className}
      action={
        action ?? (
          <Button variant="secondary" size="state" onClick={onBack ?? (() => router.history.back())}>
            Go back
          </Button>
        )
      }
    />
  );
}

/** The page, or the thing on it, isn't there (Figma P01 "Not found · 404"). */
export function NotFound({
  title = 'We can’t find that page',
  description = 'It may have moved or been deleted.',
  action,
  className,
}: {
  title?: string;
  description?: string;
  /** Replaces "Go to Home" (a link back to the list the missing thing was in). */
  action?: ReactNode;
  className?: string;
}) {
  const home = useHomePath();
  return (
    <StateBlock
      icon={Question}
      title={title}
      description={description}
      className={className}
      action={
        action ?? (
          <Link to={home} className={buttonClassName({ size: 'state' })}>
            Go to Home
          </Link>
        )
      }
    />
  );
}

/**
 * A page's data didn't load. Our API errors carry a `kind`: `forbidden` reads as no access, `not_found` as not found
 * (both are answers, so no retry), anything else (offline, a 500) as "couldn't load" with a way to try again.
 * `noun` is the thing the page is about ("task", "project"); `forbiddenHint` says whom to ask.
 */
export function LoadFailure({
  error,
  noun,
  forbiddenHint,
  notFoundHint = 'It may have been removed.',
  action,
  onRetry,
  className,
}: {
  error: unknown;
  noun: string;
  forbiddenHint?: string;
  notFoundHint?: string;
  /** A way back to the list, shown under a "no access" or "not found" answer. */
  action?: ReactNode;
  onRetry: () => void;
  className?: string;
}) {
  const kind = (error as { kind?: string } | null)?.kind;
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  return (
    <div className={className ?? 'rounded-(--mbm-component-radius-card) border border-border-line bg-surface-card'}>
      {kind === 'forbidden' ? (
        <Forbidden title={`You don’t have access to this ${noun}`} description={forbiddenHint} action={action} />
      ) : kind === 'not_found' ? (
        <NotFound title={`${Noun} not found`} description={notFoundHint} action={action} />
      ) : (
        <ErrorState title={`Couldn’t load this ${noun}`} onRetry={onRetry} />
      )}
    </div>
  );
}
